import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ProductCard } from '../components/ProductCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categoryLabels = {
  candles: 'Candles',
  wax_melts: 'Wax Melts',
  room_sprays: 'Room & Auto Sprays',
  skincare: 'Skincare',
  lipgloss: 'Lip Gloss',
};

export default function ShopPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('category') || '';

  useEffect(() => {
    const loadProducts = async () => {
      setLoading(true);
      try {
        const params = activeCategory ? `?category=${activeCategory}` : '';
        const res = await axios.get(`${API}/products${params}`);
        setProducts(res.data);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, [activeCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  const setCategory = (cat) => {
    if (cat === activeCategory) {
      setSearchParams({});
    } else {
      setSearchParams({ category: cat });
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#C4A35A] border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }
    if (products.length === 0) {
      return (
        <div className="text-center py-20">
          <p className="text-[#9A8E7A] text-sm">No products found in this category.</p>
        </div>
      );
    }
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 sm:gap-8"
        data-testid="products-grid"
      >
        {products.map(product => (
          <ProductCard key={product.id} product={product} />
        ))}
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-28 sm:pt-32 pb-16" data-testid="shop-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <p className="uppercase text-xs tracking-[0.2em] text-[#C4A35A] font-medium mb-2">Collection</p>
          <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl text-[#E8D5A3]">
            {activeCategory ? categoryLabels[activeCategory] || 'Shop' : 'All Products'}
          </h1>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-12" data-testid="category-filters">
          <button
            onClick={() => setSearchParams({})}
            className={`px-5 py-2 rounded-full text-sm transition-all duration-300 ${
              !activeCategory
                ? 'bg-[#C4A35A] text-[#0A0A0A] font-medium'
                : 'border border-[#2A2520] text-[#9A8E7A] hover:border-[#C4A35A]/40 hover:text-[#C4A35A]'
            }`}
            data-testid="filter-all"
          >
            All
          </button>
          {Object.entries(categoryLabels).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setCategory(key)}
              className={`px-5 py-2 rounded-full text-sm transition-all duration-300 ${
                activeCategory === key
                  ? 'bg-[#C4A35A] text-[#0A0A0A] font-medium'
                  : 'border border-[#2A2520] text-[#9A8E7A] hover:border-[#C4A35A]/40 hover:text-[#C4A35A]'
              }`}
              data-testid={`filter-${key}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        {renderContent()}

        <div className="text-center mt-8">
          <p className="text-sm text-[#9A8E7A]">{products.length} product{products.length !== 1 ? 's' : ''}</p>
        </div>
      </div>
    </div>
  );
}
